/* the power throw + one simulation tick — caller guards phase; env carries the React-side effects */
import axios from "axios";
import { awardFeat, awardPoints } from "../../utils/rewards";
import { API, CPU_TIERS, DEFENDER_X, GOAL_SPEED, GOAL_X, MARKERS, MATCH_FRAMES, MATES, MAX_Y, MIN_Y, PLAYER_X, REF_MAX_X, REF_MIN_X, TEAM_ORDER, TEAMS, W, WALL_BOT, WALL_TOP } from "./constants";

/* the referee's arm goes up for every stoppage, shield high for goals and timeouts */
const refSignal = (g, state, frames) => {
  g.ref.state = state;
  g.ref.timer = frames;
};

/* franchise traits — every kit carries its lore edge into the arena.
   RED FORCE reloads fast, SILVERBACKS plant heavy, VERDIGRIS blooms late,
   NIGHT RAVENS own the dark; the rest fire inline where the play happens. */
/* VS MODE — the CPU takes real drives at the user's goal (easy tier: slow
   carrier, telegraphed wind-up, 25% of shots deliberately off target) */
const handToCpu = (g) => {
  g.orb.state = "held";
  if (g.mode === "vs") g.pendingCpu = true;
};

const endCpuAttack = (g) => {
  g.possession = "player";
  g.cpu = null;
  g.cpuBall = null;
  g.orb.state = "held";
};

/* neon spark burst on a goal — the renderer colors them with the floor's glow */
const spawnSparks = (x, y) => {
  const parts = [];
  for (let i = 0; i < 46; i += 1) {
    const a = Math.random() * Math.PI * 2;
    const sp = 2 + Math.random() * 7;
    parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1.4, life: 42 + Math.random() * 42, maxLife: 84, size: 1.4 + Math.random() * 2.4 });
  }
  return parts;
};

const speedFactor = (teamKey, g) => {
  if (teamKey === "silverbacks") return 0.9;
  if (teamKey === "verdigris-vanguard") return 0.8 + 0.6 * (1 - Math.max(0, g.timeLeft) / MATCH_FRAMES);
  if (teamKey === "night-ravens") return g.lightsOut ? 1.6 : 1;
  return 1;
};

export function makeThrowOrb({ stateRef, phaseRef, playSfx, teamRef }) {
  const throwOrb = () => {
      const g = stateRef.current;
      if (phaseRef.current !== "running" || g.orb.state !== "held" || g.celebrateTimer > 0 || g.possession !== "player") return;
      // Bio Hack power throw — a hotter arm for one match; STORM CURRENT runs hotter still
      let throwSpeed = g.tubeBoost === "power" ? 18 : 13.5;
      if (teamRef?.current === "vultures") throwSpeed *= 1.18;
      // lead the moving goal slightly — easy mode generosity
      const eta = (GOAL_X - (PLAYER_X + 52)) / throwSpeed;
      const goalY = 210 + Math.sin(g.goalPhase + eta * GOAL_SPEED) * 116;
      g.orb = {
        state: "flying",
        x: PLAYER_X + 52,
        y: g.playerY - 66,
        vx: throwSpeed,
        vy: (goalY - (g.playerY - 66)) / eta,
        touched: [],
        bounces: 0,
      };
      g.traits.massUsedThrow = false; // CONCRETE MASS re-arms on every fresh throw
      g.traits.massRushed = -1;
      g.throwTimer = 26;
      playSfx("throw");
  };
  return throwOrb;
}

export function stepMatch(g, { keysRef, playSfx, teamKey, fetchHighlights, setFinalScore, setCpuScore, setHighs, setNewRecord, setSirenFlash, phaseRef, setPhase, setActiveBoost, onFullTime }) {
        // goal replay: world freeze-frames while the celebration plays
        const frozen = g.celebrateTimer > 40 || g.timeout;
        // WHITE GUARDS — PROTOCOL: the match clock starts with +6s, applied once
        if (!g.traits.clockApplied) {
          g.traits.clockApplied = true;
          if (teamKey === "white") {
            g.timeLeft += 360;
            g.banner = "PROTOCOL — +6S REGULATION EFFICIENCY";
            g.bannerTimer = 70;
          }
        }
        // NIGHT RAVENS — NIGHT SHIFT: floodlights cut for 2.5s of every 13s; the dark is theirs
        g.lightsOut = teamKey === "night-ravens" && !frozen && g.frame % 780 >= 630;
        // RED FORCE — THE RESILIENCE: turnovers reload almost twice as fast
        const reload = (n) => Math.round(n * (teamKey === "red" ? 0.55 : 1));
        if (g.replayFlash > 0) g.replayFlash -= 1;
        g.timeLeft -= frozen ? 0 : 1;
        g.goalPhase += frozen ? 0 : GOAL_SPEED;
        // final ten seconds — heartbeat + stretched audio drama
        if (g.timeLeft === 600) {
          g.banner = "FINAL 10 — LEAVE NOTHING";
          g.bannerTimer = 100;
          playSfx("heartbeat");
        }

        // player movement — Bio Hack sprint burst burns a tube for faster legs
        const moveStep = (g.tubeBoost === "sprint" ? 8.4 : 5.2) * speedFactor(teamKey, g);
        const up = keysRef.current["ArrowUp"] || keysRef.current["KeyW"];
        const down = keysRef.current["ArrowDown"] || keysRef.current["KeyS"];
        if (!frozen) {
          g.playerMoving = false;
          if (up) {
            g.playerY = Math.max(MIN_Y, g.playerY - moveStep);
            g.playerMoving = true;
          }
          if (down) {
            g.playerY = Math.min(MAX_Y, g.playerY + moveStep);
            g.playerMoving = true;
          }
        }
        if (g.throwTimer > 0) g.throwTimer -= 1;
        if (g.celebrateTimer > 0) g.celebrateTimer -= 1;
        if (g.crowdSurge > 0) g.crowdSurge -= 1;
        if (g.defenderFallTimer > 0) g.defenderFallTimer -= 1;
        if (g.defenderCatchTimer > 0) g.defenderCatchTimer -= 1;
        if (g.bannerTimer > 0) g.bannerTimer -= 1;
        if (g.cpuNetRipple > 0) g.cpuNetRipple -= 1;
        // VS shot clock — hold the ball ~8s and the defense strips it loose
        if (g.mode === "vs" && g.possession === "player" && g.orb.state === "held" && !frozen) {
          g.holdTimer += 1;
          if (g.holdTimer === 300) {
            g.banner = "SHOT CLOCK WINDING — THROW IT";
            g.bannerTimer = 60;
            playSfx("siren", 0.3);
          }
          if (g.holdTimer >= 480) {
            g.holdTimer = 0;
            handToCpu(g);
            g.banner = "STRIPPED — SHOT CLOCK VIOLATION";
            g.bannerTimer = 60;
            refSignal(g, "foul", 64);
            playSfx("blocked");
          }
        } else if (g.orb.state !== "held" || g.possession !== "player") {
          g.holdTimer = 0;
        }

        // referee — signals hold through freezes; otherwise he paces the touchline
        const ref = g.ref;
        if (ref.timer > 0) ref.timer -= 1;
        if (g.timeout && ref.state !== "shield") {
          ref.state = "shield";
          ref.timer = 2; // re-armed every tick while the whistle holds
        } else if (ref.state === "foul" || ref.state === "shield") {
          if (ref.timer <= 0) {
            ref.state = "idle";
            ref.timer = 50;
          }
        } else if (!frozen) {
          if (ref.state === "idle") {
            if (ref.timer <= 0) {
              ref.state = "walk";
              ref.dir = ref.x < (REF_MIN_X + REF_MAX_X) / 2 ? 1 : -1;
            }
          } else {
            ref.x += ref.dir * 0.55;
            if (ref.x <= REF_MIN_X || ref.x >= REF_MAX_X) {
              ref.x = Math.max(REF_MIN_X, Math.min(REF_MAX_X, ref.x));
              ref.dir *= -1;
              ref.state = "idle";
              ref.timer = 70 + Math.floor(Math.random() * 80);
            }
          }
        }

        // electronic goal position
        const goalY = 210 + Math.sin(g.goalPhase) * 116;

        // defender tracks the goal at easy-league speed
        if (!frozen) {
          const target = goalY;
          g.defenderY += (target - g.defenderY) * 0.045;
          g.defenderY = Math.max(MIN_Y, Math.min(MAX_Y, g.defenderY));
          // rival markers shadow their squadmate with a lag, drifting enough to threaten gap lanes
          MARKERS.forEach((marker, i) => {
            const mate = MATES[marker.mark];
            const markTarget =
              mate.y + Math.sin(g.frame * 0.05 + marker.mark * 1.7) * 7 + marker.yOff + Math.sin(g.frame * 0.011 + i * 2.2) * 30;
            g.markY[i] += (markTarget - g.markY[i]) * 0.06;
            g.markY[i] = Math.max(MIN_Y, Math.min(MAX_Y, g.markY[i]));
          });
        }

        // orb flight
        if (!frozen && g.orb.state === "flying") {
          g.orb.x += g.orb.vx;
          g.orb.y += g.orb.vy;
          // squad zip passes — mates one-touch the ball onward, faster every touch
          for (let mi = 0; mi < MATES.length; mi += 1) {
            if (g.orb.touched.includes(mi)) continue;
            const mate = MATES[mi];
            const bobY = mate.y + Math.sin(g.frame * 0.05 + mi * 1.7) * 7;
            // AURORA SYNDICATE — STATIC LINK: one shared mind, a far wider touch window
            const zipX = teamKey === "aurora-syndicate" ? 44 : 30;
            const zipY = teamKey === "aurora-syndicate" ? 64 : 48;
            if (Math.abs(g.orb.x - mate.x) < zipX && Math.abs(g.orb.y - bobY) < zipY) {
              const next = MATES.map((m, i) => ({ ...m, i })).filter((m) => m.x > mate.x + 10 && !g.orb.touched.includes(m.i))[0];
              const speed = Math.min(34, Math.hypot(g.orb.vx, g.orb.vy) * 1.6);
              let tx;
              let ty;
              if (next) {
                tx = next.x;
                ty = next.y + Math.sin(g.frame * 0.05 + next.i * 1.7) * 7;
              } else {
                // last touch fires at the goal with a perfect lead — the zip the keeper can't read
                const eta = Math.max(1, (GOAL_X - g.orb.x) / speed);
                tx = GOAL_X;
                ty = 210 + Math.sin(g.goalPhase + eta * GOAL_SPEED) * 116;
              }
              const dx = tx - g.orb.x;
              const dy = ty - g.orb.y;
              const len = Math.max(1, Math.hypot(dx, dy));
              g.orb.vx = (dx / len) * speed;
              g.orb.vy = (dy / len) * speed;
              g.orb.touched.push(mi);
              g.banner = `ZIP PASS ×${g.orb.touched.length}`;
              g.bannerTimer = 26;
              playSfx("throw", 1.5);
              break;
            }
          }
          // live walls — bank shots stay in play at an angle
          // OBSIDIAN LEGION — THE COIL: banks come off the walls hotter than they went in
          const bank = teamKey === "obsidian-legion" ? 1.12 : 0.92;
          if (g.orb.y < WALL_TOP) {
            g.orb.y = WALL_TOP;
            g.orb.vy = Math.abs(g.orb.vy) * bank;
            g.orb.bounces += 1;
            playSfx("blocked", 1.6);
          }
          if (g.orb.y > WALL_BOT) {
            g.orb.y = WALL_BOT;
            g.orb.vy = -Math.abs(g.orb.vy) * bank;
            g.orb.bounces += 1;
            playSfx("blocked", 1.6);
          }
          if (g.orb.x < 56) {
            g.orb.x = 56;
            g.orb.vx = Math.abs(g.orb.vx) * 0.9;
            g.orb.bounces += 1;
            playSfx("blocked", 1.6);
          }
          if (g.orb.bounces > 6) {
            handToCpu(g);
            g.banner = "BALL DEAD — RELOAD";
            g.bannerTimer = reload(40);
            refSignal(g, "foul", 64);
          }
          // rival markers shut down lazy direct throws — zip passes blow straight past them
          if (g.orb.touched.length === 0) {
            for (let i = 0; i < MARKERS.length; i += 1) {
              if (Math.abs(g.orb.x - MARKERS[i].x) < 26 && Math.abs(g.orb.y - g.markY[i]) < 42) {
                if (g.traits.massRushed === i) continue; // already bull-rushed this marker this throw
                // SILVERBACKS — CONCRETE MASS: the first marker hit of a throw gets bull-rushed
                if (teamKey === "silverbacks" && !g.traits.massUsedThrow) {
                  g.traits.massUsedThrow = true;
                  g.traits.massRushed = i;
                  g.banner = "CONCRETE MASS — BULL-RUSHED THROUGH";
                  g.bannerTimer = 46;
                  playSfx("blocked", 0.7);
                  break;
                }
                // IRON HEARTS — THE BROKEN VALENTINE: once a match the drive-killer is shrugged off
                if (teamKey === "iron-hearts" && !g.traits.heartUsed) {
                  g.traits.heartUsed = true;
                  g.orb.touched.push(99); // the save counts as a touch — markers can't down this drive
                  g.banner = "THE BROKEN VALENTINE — CRACKED, NOT BROKEN";
                  g.bannerTimer = 70;
                  refSignal(g, "foul", 64);
                  playSfx("blocked", 0.8);
                  break;
                }
                handToCpu(g);
                g.banner = "MARKED DOWN — ZIP IT PAST THEM";
                g.bannerTimer = reload(46);
                refSignal(g, "foul", 64);
                playSfx("blocked");
                break;
              }
            }
          }
          // defender block — but a two-touch zip is too hot to handle
          if (g.orb.touched.length < 2 && g.defenderFallTimer <= 0 && Math.abs(g.orb.x - DEFENDER_X) < 34 && Math.abs(g.orb.y - (g.defenderY - 56)) < 52) {
            // CHROME PHANTOMS — GHOST PROTOCOL: once a match the block phases through the decoy
            if (teamKey === "chrome-phantoms" && !g.traits.ghostUsed) {
              g.traits.ghostUsed = true;
              g.orb.touched.push(98, 99); // the decoy flight is untouchable for the rest of this pass
              g.banner = "GHOST PROTOCOL — PHASED THROUGH";
              g.bannerTimer = 60;
              playSfx("throw", 1.5);
            // IRON HEARTS — THE BROKEN VALENTINE: the one save works against the keeper too
            } else if (teamKey === "iron-hearts" && !g.traits.heartUsed) {
              g.traits.heartUsed = true;
              g.orb.touched.push(99, 100); // two-touch hot — nothing catches this drive now
              g.banner = "THE BROKEN VALENTINE — CRACKED, NOT BROKEN";
              g.bannerTimer = 70;
              refSignal(g, "foul", 64);
              playSfx("blocked", 0.8);
            } else {
              handToCpu(g);
              g.defenderCatchTimer = reload(34);
              g.banner = "BLOCKED BY THE DEFENSE";
              g.bannerTimer = reload(50);
              refSignal(g, "foul", 64);
              playSfx("blocked");
            }
          } else if (g.orb.x >= GOAL_X - 12) {
            if (Math.abs(g.orb.y - goalY) < 34) {
              g.score += 1;
              g.orb.state = "held";
              g.celebrateTimer = 90;
              if (g.mode === "vs") g.pendingCpu = true;
              g.defenderFallTimer = 80;
              g.replayFlash = 14;
              g.goalSparks = spawnSparks(GOAL_X, goalY);
              g.crowdSurge = 80; // the stands surge and the flags go up
              g.banner = "GOAL! +1";
              g.bannerTimer = 80;
              refSignal(g, "shield", 96);
              playSfx("goal");
              // the crowd eruption stretches with the freeze-frame replay
              playSfx("crowd", g.timeLeft <= 600 ? 0.4 : 0.62);
            } else if (g.orb.x > GOAL_X + 60) {
              handToCpu(g);
              g.banner = "WIDE — RELOAD";
              g.bannerTimer = reload(46);
              refSignal(g, "foul", 64);
            }
          }
          if (g.orb.x > W + 40) handToCpu(g);
        }

        // VS MODE — CPU attack: carrier walks in, telegraphs, shoots at YOUR goal
        if (g.mode === "vs") {
          const T = CPU_TIERS[g.tier] || CPU_TIERS.easy;
          g.cpuGoalPhase += frozen ? 0 : GOAL_SPEED * T.goalDrift; // your goal drifts with the tier
          g.cpuGoalY = 210 + Math.sin(g.cpuGoalPhase) * 110;
          if (g.pendingCpu && g.celebrateTimer <= 0 && !g.timeout) {
            g.pendingCpu = false;
            g.possession = "cpu";
            // the carrier takes the lane AWAY from you — reads are earned on foot, not camped
            const awayLane = g.playerY <= 210 ? 250 + Math.random() * 120 : 80 + Math.random() * 120;
            g.cpu = { phase: "drive", timer: 130, x: 850, y: awayLane, combos: T.combos || 0, heat: 0, moveTo: null };
            g.banner = `CPU DRIVE — ${T.label} TIER`;
            g.bannerTimer = 56;
            playSfx("heartbeat", 0.5);
          }
          if (g.possession === "cpu" && g.cpu && !frozen) {
            const c = g.cpu;
            c.timer -= 1;
            // LANE READ — track the carrier's lane and pick the ball clean off them.
            // Higher tiers shrink the window and the read range.
            const readLane = Math.abs(g.playerY - c.y) < T.stealRange;
            const stealable = c.phase === "windup" || (c.phase === "drive" && c.x <= T.stealZoneX);
            if (stealable && readLane) {
              g.steals += 1;
              g.banner = "PICKED OFF — YOU CAUGHT THEIR BALL · +2 PTS";
              g.bannerTimer = 70;
              refSignal(g, "shield", 70);
              playSfx("blocked", 1.2);
              playSfx("crowd", 0.3);
              endCpuAttack(g);
            } else if (c.phase === "drive") {
              // glide to the receiver spot after a combo catch (pass-and-move)
              if (c.moveTo) {
                c.x += (c.moveTo.x - c.x) * 0.22;
                c.y += (c.moveTo.y - c.y) * 0.22;
                if (Math.hypot(c.moveTo.x - c.x, c.moveTo.y - c.y) < 8) c.moveTo = null;
              } else {
                c.x -= T.carrierSpeed;
                c.y = Math.max(MIN_Y, Math.min(MAX_Y, c.y + Math.sin(g.frame * 0.05) * 1.1));
              }
              // COMBO BUILD-UP — out wide they zip it between their own before driving in;
              // every completed pass makes the eventual shot hotter. The FIRST pass of an
              // attack is often a LONG SWITCH to a deep runner — slower, telegraphed,
              // and cut-able if you hold the crossing lane.
              if (!c.moveTo && c.combos > 0 && c.x > T.stealZoneX + 40) {
                c.phase = "combo";
                c.comboLong = c.heat === 0 && Math.random() < 0.55;
                const mi = Math.floor(Math.random() * MARKERS.length);
                c.comboTo = c.comboLong
                  ? { x: 230 + Math.random() * 70, y: 92 + Math.random() * 236 }
                  : { x: MARKERS[mi].x, y: g.markY[mi] - 20 };
                const dx = c.comboTo.x - (c.x - 34);
                const dy = c.comboTo.y - (c.y - 62);
                const len = Math.max(1, Math.hypot(dx, dy));
                const spd = (T.comboSpeed || 13) * (c.comboLong ? 0.72 : 1);
                g.cpuBall = { x: c.x - 34, y: c.y - 62, vx: (dx / len) * spd, vy: (dy / len) * spd };
                if (c.comboLong) {
                  g.banner = "LONG SWITCH — CUT THE LANE";
                  g.bannerTimer = 46;
                  playSfx("siren", 0.25);
                } else {
                  playSfx("throw", 1.1);
                }
              } else if (c.timer <= 0 || c.x <= 620) {
                c.phase = "windup";
                c.timer = T.windupFrames;
                g.banner = c.heat > 0 ? "CPU COMBO SHOT — READ THE RELEASE" : "CPU WIND-UP — READ THE SHOT";
                g.bannerTimer = Math.min(34, T.windupFrames + 4);
                playSfx("siren", 0.35);
              }
            } else if (c.phase === "combo" && g.cpuBall) {
              // the zip between their own — catch it and the receiver becomes the carrier
              const b = g.cpuBall;
              b.x += b.vx;
              b.y += b.vy;
              // LANE CUT — hold the crossing lane and pick the long switch out of the air
              if (c.comboLong && Math.abs(b.x - (PLAYER_X + 46)) < 30 && Math.abs(b.y - (g.playerY - 66)) < 50) {
                g.intercepts += 1;
                g.banner = "CUT OUT OF THE AIR — +3 PTS";
                g.bannerTimer = 70;
                refSignal(g, "shield", 70);
                playSfx("blocked", 1.2);
                playSfx("crowd", 0.35);
                endCpuAttack(g);
              } else if (Math.hypot(b.x - c.comboTo.x, b.y - c.comboTo.y) <= Math.max(14, T.comboSpeed || 13)) {
                g.cpuBall = null;
                c.combos -= 1;
                c.heat += 1;
                c.moveTo = c.comboLong
                  ? { x: Math.max(240, c.comboTo.x + 30), y: Math.max(MIN_Y + 40, Math.min(MAX_Y - 20, c.comboTo.y + 62)) }
                  : { x: Math.max(560, c.comboTo.x - 30), y: Math.max(MIN_Y + 40, Math.min(MAX_Y - 20, c.comboTo.y + 62)) };
                g.banner = `CPU ZIP ×${c.heat} — CUT IT OUT`;
                g.bannerTimer = 30;
                playSfx("throw", 1.3);
                c.phase = "drive";
                if (c.combos <= 0) c.timer = Math.min(c.timer, 30); // straight into the drive after the last catch
              }
            } else if (c.phase === "windup") {
              if (c.timer <= 0) {
                c.phase = "ball";
                // combo build-up makes the release hotter and truer
                const hotSpeed = T.shotSpeed * (1 + 0.2 * (c.heat || 0));
                const frames = Math.max(1, (c.x - 40 - 60) / hotSpeed);
                // lead the drifting goal — read where the light WILL be, not where it is
                const leadY = 210 + Math.sin(g.cpuGoalPhase + frames * GOAL_SPEED * T.goalDrift) * 110;
                const miss = Math.random() < T.missChance * (1 - 0.2 * (c.heat || 0));
                let targetY = leadY + (miss ? (Math.random() < 0.5 ? -1 : 1) * (64 + Math.random() * 30) : (Math.random() - 0.5) * 24);
                // trick tiers bank shots off the walls — aim at the goal's mirror image
                if (!miss && Math.random() < T.bankChance) {
                  const wall = targetY < 210 ? WALL_BOT : WALL_TOP;
                  targetY = 2 * wall - targetY;
                }
                g.cpuBall = { x: c.x - 40, y: c.y - 62, vx: -hotSpeed, vy: (targetY - (c.y - 62)) / frames };
                playSfx("throw", 0.85);
              }
            } else if (c.phase === "ball" && g.cpuBall) {
              const b = g.cpuBall;
              b.x += b.vx;
              b.y += b.vy;
              if (b.y < WALL_TOP) {
                b.y = WALL_TOP;
                b.vy = Math.abs(b.vy) * 0.9;
              }
              if (b.y > WALL_BOT) {
                b.y = WALL_BOT;
                b.vy = -Math.abs(b.vy) * 0.9;
              }
              // the user is the keeper — slide into the lane to catch the shot
              if (b.x <= PLAYER_X + 46 && b.x >= PLAYER_X - 34 && Math.abs(b.y - (g.playerY - 66)) < 54) {
                g.banner = "SAVED — THE KEEPER HOLDS IT";
                g.bannerTimer = 60;
                refSignal(g, "shield", 70);
                playSfx("blocked");
                endCpuAttack(g);
              } else if (b.x <= 60) {
                if (Math.abs(b.y - g.cpuGoalY) < 44) {
                  g.cpuScore += 1;
                  // the goal is SEEN going in — net ripple, freeze-frame replay, away-end roar
                  g.cpuNetRipple = 34;
                  g.replayFlash = 14;
                  g.celebrateTimer = 70;
                  g.cpuGoalSparks = spawnSparks(60, g.cpuGoalY);
                  g.crowdSurge = 55; // a muted surge from the away end
                  g.banner = "CPU SCORES — ANSWER BACK";
                  g.bannerTimer = 78;
                  refSignal(g, "shield", 84);
                  playSfx("siren", 0.7);
                  playSfx("crowd", 0.28);
                } else {
                  g.banner = "CPU MISS — YOUR BALL";
                  g.bannerTimer = 50;
                  playSfx("blocked", 0.6);
                }
                endCpuAttack(g);
              }
            }
          }
        }

        // full time
        if (g.timeLeft <= 0) {
          setFinalScore(g.score);
          if (setCpuScore) setCpuScore(g.cpuScore);
          const key = TEAMS[teamKey].highKey;
          const prev = Number(localStorage.getItem(key) || 0);
          if (g.score > prev) localStorage.setItem(key, String(g.score));
          // submit to the league-wide record desk
          axios
            .post(`${API}/riotball/record`, { team: teamKey, score: g.score })
            .then(({ data }) => {
              setHighs(Object.fromEntries(TEAM_ORDER.map((t) => [t, data[t] || 0])));
              window.dispatchEvent(new Event("riotball-records-changed"));
              setNewRecord(Boolean(data.record));
              if (data.record) {
                playSfx("siren");
                setSirenFlash(true);
                window.setTimeout(() => setSirenFlash(false), 1900);
                fetchHighlights();
              }
            })
            .catch(() => {
              setHighs((h) => ({ ...h, [teamKey]: Math.max(h[teamKey] || 0, g.score) }));
              setNewRecord(g.score > prev);
              if (g.score > prev) playSfx("siren");
            });
          phaseRef.current = "over";
          setPhase("over");
          setActiveBoost(null);
          // base purse for the goals, lane-read + lane-cut bonuses on top, tier bounty for a VS win
          const base = Math.min(50, 2 + g.score * 2 + g.steals * 2 + (g.intercepts || 0) * 3);
          const bounty = g.mode === "vs" && g.score > g.cpuScore ? (CPU_TIERS[g.tier]?.winBonus || 0) : 0;
          awardPoints("riotball", base + bounty);
          if (g.score >= 3) awardFeat("HAT TRICK");
          onFullTime?.(g);
        }
}
